import Phaser from "phaser";
import { ReplayEventFilter } from "../../net/event-filter";
import type { OnlineMatch } from "../../net/online-match";
import { packControls, unpackControls } from "../../net/protocol";
import {
	type ControlState,
	createWorld,
	type GameMode,
	loadMap,
	type MapDef,
	type MatchResult,
	type MatchRules,
	matchResult,
	NO_CONTROLS,
	type PlayerIndex,
	step,
	TICK_MS,
	type WorldState,
} from "../../sim";
import { type AiState, aiControls, createAi } from "../../sim/ai/ai-pilot";
import { AI_DIFFICULTY, type AiDifficulty } from "../../sim/ai/difficulty";
import { hashWorld } from "../../sim/hash";
import { el, isTypingTarget } from "../../ui/dom";
import { MatchHud } from "../../ui/match-hud";
import { MAPS, type MapKey } from "../assets";
import { AudioPlayer } from "../audio/audio-player";
import { mergeControls } from "../input/bindings";
import { demoControls } from "../input/demo-controls";
import { PlayerInput } from "../input/player-input";
import { TouchControls } from "../input/touch";
import { FixedStepLoop } from "../loop/fixed-step";
import { FrameStats } from "../loop/frame-stats";
import { registerAnimations } from "../render/animations";
import { type FreeCamera, fitWorld, panFreeCamera, zoomFreeCamera } from "../render/camera";
import { CorrectionSmoother } from "../render/correction";
import { HudView } from "../render/hud-view";
import { Interpolator } from "../render/interpolation";
import { splitViewports, viewersFor } from "../render/layout";
import { ViewportView } from "../render/viewport-view";
import { loadSettings, type Settings } from "../settings";
import { SceneKey } from "./scene-keys";

/** Callbacks from a running match to the game flow (UI). */
export interface MatchHooks {
	/** The match finished `create()` and is running. */
	onReady?(scene: MatchScene): void;
	onPauseChange?(paused: boolean): void;
	/** The match is decided; the sim has stopped. */
	onGameOver?(result: MatchResult, world: WorldState): void;
	/** A replay played to its end. */
	onReplayEnd?(): void;
}

/** Keep simulating this long after the deciding moment so the final explosion plays out. */
const GAME_OVER_DELAY_TICKS = 100;

export interface MatchSceneData {
	map?: MapKey;
	hooks?: MatchHooks;
	playerCount?: 1 | 2 | 3 | 4;
	/** duel (default) or single-player asteroid survival. */
	mode?: GameMode;
	/** Gameplay rules; classic (the original) when omitted. */
	rules?: MatchRules;
	/** Computer-controlled players and their difficulty. */
	ai?: Array<{ player: PlayerIndex; difficulty: AiDifficulty }>;
	/** Fixed seed for reproducible matches (tests); random otherwise. */
	seed?: number;
	/**
	 * Menu backdrop: AI against AI, no input, HUD, sound or pausing; a finished match
	 * restarts with a new seed.
	 */
	attract?: boolean;
	/**
	 * Online match (P9.4): map, rules and seed come from its setup; one local player flies
	 * slot `online.slot`, the other is the remote player. No pausing and no AI.
	 */
	online?: OnlineMatch;
	/**
	 * Replay playback (P10.6): every player's recorded inputs replace live input; all pilots
	 * are shown. `startTick` fast-forwards silently (jumps restart the scene with one).
	 */
	replay?: { inputs: number[][]; startTick?: number; freeCamera?: FreeCamera };
	/** A player-made map (P11.1) instead of the built-in `map`. */
	customMap?: MapDef;
	/** Survival with the 2008 spec's scoring (P11.5). */
	survivalScoring?: "asteroids" | "2008";
	/** Pilot names by player (replays); local matches read them from the settings (P11.8). */
	names?: string[];
}

/** Playback speeds offered by ↑/↓ in a replay. */
const REPLAY_SPEEDS = [0.5, 1, 2, 4] as const;
/** ←/→ in a replay jump this many ticks (10 s). */
const REPLAY_JUMP_TICKS = Math.round(10_000 / TICK_MS);
/** Free replay camera (I-014): WASD pans this many screen pixels a second; +/− zoom steps. */
const FREE_CAMERA_PAN_PER_S = 900;
const FREE_CAMERA_ZOOM_STEP = 1.25;
/** Mouse wheel: zoom factor per wheel pixel. */
const FREE_CAMERA_WHEEL = 0.0015;
const PAN_KEYS: Record<string, [number, number]> = {
	KeyW: [0, -1],
	KeyA: [-1, 0],
	KeyS: [0, 1],
	KeyD: [1, 0],
};

/** What a finished match needs to become a replay (P10.6). */
export interface MatchRecording {
	map: MapKey;
	seed: number;
	mode: GameMode;
	rules: MatchRules;
	inputs: number[][];
	customMap?: MapDef;
}

/**
 * Runs a match: drives the deterministic sim at a fixed 15 ms step from Phaser's variable
 * frame loop and renders one ViewportView per player plus the HUD.
 */
export class MatchScene extends Phaser.Scene {
	private world!: WorldState;
	private loop!: FixedStepLoop;
	private interp!: Interpolator;
	private views: ViewportView[] = [];
	private hud!: HudView;
	private playerInput!: PlayerInput;
	private readControls!: () => ControlState[];
	private ais: AiState[] = [];
	private readonly frameStats = new FrameStats();
	private viewers: PlayerIndex[] = [];
	private hooks: MatchHooks = {};
	private statsHud: MatchHud | null = null;
	private attract = false;
	private audio: AudioPlayer | null = null;
	private paused = false;
	private result: MatchResult | null = null;
	private resultTick = 0;
	private names: string[] = [];
	private ended = false;
	private online: OnlineMatch | null = null;
	private touch: TouchControls | null = null;
	private corrections: CorrectionSmoother | null = null;
	private startData: MatchSceneData = {};
	private setup!: { map: MapKey; seed: number; mode: GameMode; customMap?: MapDef };
	/** Inputs of every step so far, per player (packed), for the replay. */
	private recorded: number[][] = [];
	private replay: { inputs: number[][]; length: number } | null = null;
	private replaySpeed = 1;
	private replayPaused = false;
	private replayBar: HTMLElement | null = null;
	/** Replays: the viewer's own camera over the whole screen (I-014); null = follow the pilots. */
	private freeCam: FreeCamera | null = null;
	/** Pan keys held down (free camera). */
	private readonly panKeys = new Set<string>();
	/** Touch pinch: the last distance between two fingers. */
	private pinch: number | null = null;
	/** Key sets read by PlayerInput (rebuilt when settings change mid-match). */
	private humanInputs = 0;

	constructor() {
		super(SceneKey.Match);
	}

	create(data: MatchSceneData): void {
		registerAnimations(this);
		this.hooks = data.hooks ?? {};
		this.attract = data.attract === true;
		if (this.attract) {
			data.ai = [
				{ player: 0, difficulty: "normal" },
				{ player: 1, difficulty: "hard" },
			];
			data.playerCount = 2;
		}
		this.paused = false;
		this.result = null;
		this.ended = false;
		this.online = data.online ?? null;
		this.corrections = null;
		this.startData = data;
		this.replay = data.replay
			? { inputs: data.replay.inputs, length: Math.min(...data.replay.inputs.map((i) => i.length)) }
			: null;
		this.replayPaused = false;
		this.freeCam = data.replay?.freeCamera ? { ...data.replay.freeCamera } : null;
		this.panKeys.clear();
		this.pinch = null;
		if (this.replay) data.ai = [];
		if (this.online) {
			const { map, rules, seed, customMap, mode, players } = this.online.setup;
			Object.assign(data, {
				map,
				rules,
				seed,
				customMap,
				playerCount: Math.min(4, Math.max(2, players ?? 2)) as 2 | 3 | 4,
				mode: mode ?? "duel",
				ai: [],
			});
		}

		const mapKey = data.map ?? "classic";
		const map = data.customMap ?? loadMap(this.cache.text.get(mapKey) as string, MAPS[mapKey]);
		const seed = data.seed ?? Math.floor(Math.random() * 2 ** 32);
		this.world = createWorld(map, {
			playerCount: data.playerCount ?? 2,
			mode: data.mode ?? "duel",
			seed,
			...(data.rules ? { rules: data.rules } : {}),
			...(data.survivalScoring ? { survivalScoring: data.survivalScoring } : {}),
		});
		this.loop = new FixedStepLoop(TICK_MS);
		this.interp = new Interpolator();
		this.setup = {
			map: mapKey,
			seed,
			mode: data.mode ?? "duel",
			...(data.customMap ? { customMap: data.customMap } : {}),
		};

		const players = this.world.players.length;
		this.recorded = Array.from({ length: players }, () => []);
		if (this.replay) {
			// Jumps: silently fast-forward to the start tick before any view exists.
			const start = Math.min(data.replay?.startTick ?? 0, this.replay.length);
			while (this.world.tick < start) step(this.world, this.replayControls());
		}
		const params = new URLSearchParams(window.location.search);
		const aiPlayers = (data.ai ?? []).map((a) => a.player);
		this.ais = (data.ai ?? []).map((a, i) =>
			createAi(a.player, AI_DIFFICULTY[a.difficulty], (seed + 7919 * (i + 1)) >>> 0),
		);
		this.viewers = this.online ? [this.online.slot] : viewersFor(players, aiPlayers);
		// Humans are the lowest player indices, so the first N key bindings apply.
		this.humanInputs = this.replay ? 0 : this.online ? 2 : players - aiPlayers.length;
		this.playerInput = new PlayerInput(this.humanInputs, loadSettings().bindings);
		this.names = this.pilotNamesFor(data, players, aiPlayers);
		// `?demo` swaps human input for the scripted attract-mode pilots.
		this.readControls = params.has("demo")
			? () => demoControls(this.world.tick, players)
			: () => this.playerInput.read();
		if (this.online) {
			// Online the one local pilot may use either key set (and the first gamepad).
			const read = this.readControls;
			this.readControls = () => [mergeControls(...read())];
		}
		// On-screen controls (phones, tablets) whenever one local pilot has the whole screen.
		this.touch = null;
		if (!this.attract && !this.replay && this.viewers.length === 1 && !params.has("demo")) {
			const ui = document.getElementById("ui-root") ?? document.body;
			const touch = new TouchControls(ui, loadSettings().touchControls, () => this.setPaused(true));
			this.touch = touch;
			const read = this.readControls;
			this.readControls = () => {
				const [own, ...rest] = read();
				return [own ? mergeControls(own, touch.read()) : touch.read(), ...rest];
			};
		}

		// Camera order is render order: world viewports first, HUD on top.
		const layouts = splitViewports(this.scale.width, this.scale.height, this.viewers);
		this.views = layouts.map((layout, i) => {
			const camera = i === 0 ? this.cameras.main : this.cameras.add();
			const view = new ViewportView(this, camera, layout.player, layout, this.world);
			// Attract mode: wider shot with the action left of the menu panel.
			if (this.attract) view.framing = { zoomScale: 0.6, offsetX: 0.28 };
			const { lookAhead, speedZoom } = loadSettings();
			view.cameraStyle = { lookAhead, speedZoom };
			if (!this.attract && loadSettings().pilotMarkers) {
				view.markers = new Map(
					this.world.players.map((p) => [
						p.index,
						aiPlayers.includes(p.index) ? "AI" : `P${p.index + 1}`,
					]),
				);
			}
			return view;
		});
		this.hud = new HudView(this, this.cameras.add(), this.world);
		this.hud.setLayout(this.scale.width, this.scale.height, layouts);
		this.isolateLayers();
		if (this.replay) {
			this.applyReplayCamera();
			this.attachFreeCameraPointer();
		}

		// Opt-in debug hook (`?debug`) for manual inspection and e2e scenario setup.
		if (params.has("debug") && !this.attract) {
			(window as unknown as { gravityDebug: unknown }).gravityDebug = {
				world: this.world,
				soundsPlaying: () => this.sound.getAllPlaying().length,
				enginesPlaying: () => this.sound.getAllPlaying().filter((x) => x.key === "engine").length,
				webAudio: this.sound instanceof Phaser.Sound.WebAudioSoundManager,
				frameStats: () => this.frameStats.summary(),
				resetFrameStats: () => this.frameStats.reset(),
				hash: () => hashWorld(this.world),
				/** Replays: the free camera (I-014), or null while following the pilots. */
				freeCamera: () => (this.freeCam ? { ...this.freeCam } : null),
				/**
				 * Tests: run up to `ticks` offline ticks now, without drawing, with no human input
				 * (AI pilots still fly), so a test doesn't depend on how fast the browser renders.
				 * Stops at game over; returns the world tick.
				 */
				advance: (ticks: number) => {
					for (let i = 0; i < ticks && !this.online && !this.ended; i++) {
						if (!this.stepLocal([])) break;
					}
					document.body.dataset.simTick = String(this.world.tick);
					return this.world.tick;
				},
				/** Tests: end the match now on this client, as if player 1 (blue) had won. */
				forceGameOver: () => {
					if (this.ended) return;
					this.ended = true;
					this.hooks.onGameOver?.({ kind: "winner", winner: 0 }, this.world);
				},
				online: () =>
					this.online
						? {
								slot: this.online.slot,
								status: this.online.current.kind,
								rtt: this.online.rtt,
								confirmedTick: this.online.confirmedTick,
								desyncTick: this.online.desyncTick,
								...this.online.stats,
							}
						: null,
			};
		}

		if (this.attract) {
			this.hud.layer.setVisible(false); // just the ships behind the menu
		} else {
			// Sounds pan towards the viewport they happen in (red left, blue right when split).
			const listeners = this.viewers.map((player, i) => ({
				player,
				// Each viewport's side of the screen (left or right column).
				bias: this.viewers.length >= 2 ? (i % 2 === 0 ? -0.5 : 0.5) : 0,
			}));
			this.audio = new AudioPlayer(this, listeners);
			this.statsHud = new MatchHud(
				document.getElementById("ui-root") ?? document.body,
				this.world,
				{
					// Viewports first (matching the split), then pilots without one (AI in a party).
					order:
						this.viewers.length >= 2
							? [
									...this.viewers,
									...this.world.players
										.map((p) => p.index)
										.filter((i) => !this.viewers.includes(i)),
								]
							: undefined,
					quad: this.viewers.length > 2,
					ai: aiPlayers,
					names: this.names,
				},
			);
			// Losing focus or hiding the tab pauses (never auto-resumes). Esc is routed
			// through the UI's ScreenManager so the pause menu can own it.
			window.addEventListener("keydown", this.onKeyDown);
			window.addEventListener("keyup", this.onKeyUp);
			if (this.replay) {
				this.replayBar = el("div", { class: "replay-bar", role: "status" });
				(document.getElementById("ui-root") ?? document.body).append(this.replayBar);
			}
			this.game.events.on(Phaser.Core.Events.BLUR, this.pause, this);
			this.game.events.on(Phaser.Core.Events.HIDDEN, this.pause, this);
		}

		this.scale.on(Phaser.Scale.Events.RESIZE, this.onResize, this);
		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.scale.off(Phaser.Scale.Events.RESIZE, this.onResize, this);
			this.game.events.off(Phaser.Core.Events.BLUR, this.pause, this);
			this.game.events.off(Phaser.Core.Events.HIDDEN, this.pause, this);
			window.removeEventListener("keydown", this.onKeyDown);
			window.removeEventListener("keyup", this.onKeyUp);
			this.playerInput.destroy();
			this.touch?.destroy();
			this.touch = null;
			this.replayBar?.remove();
			this.replayBar = null;
			this.audio?.destroy();
			this.statsHud?.destroy();
			this.audio = null;
			this.statsHud = null;
		});

		if (this.online) this.attachOnline(this.online);
		this.hooks.onReady?.(this);
	}

	/** Routes the rollback session's steps into the same views, audio and interpolation. */
	private attachOnline(online: OnlineMatch): void {
		const filter = new ReplayEventFilter();
		const corrections = new CorrectionSmoother();
		this.corrections = corrections;
		for (const view of this.views) view.corrections = corrections;
		online.attach(this.world, {
			beforeStep: (world) => this.interp.capture(world),
			afterStep: (events, tick, replay) => {
				const shown = filter.filter(events, tick, replay);
				for (const ev of shown) for (const view of this.views) view.handleEvent(ev);
				this.audio?.handleEvents(shown, this.world);
			},
			beforeRollback: (world) => corrections.capture(world),
			afterRollback: () => {
				corrections.corrected(this.world);
				for (const view of this.views) view.resync(this.world);
				// A predicted result may not hold in the corrected timeline.
				if (this.result && !matchResult(this.world)) this.result = null;
			},
		});
	}

	override update(_time: number, delta: number): void {
		const t0 = performance.now();
		// Online matches can't pause: the opponent keeps playing.
		const speed = this.replay ? (this.replayPaused ? 0 : this.replaySpeed) : 1;
		const steps =
			(this.paused && !this.online) || this.ended ? 0 : this.loop.advance(delta * speed);
		// Online the match runs on behind the menu: no steering while it is open.
		const human = steps > 0 && !this.paused ? this.readControls() : [];
		if (this.online) {
			const local = human[0] ?? NO_CONTROLS;
			for (let i = 0; i < steps; i++) {
				if (this.online.advance(local) && this.checkGameOver()) break;
			}
			this.online.flush();
			this.corrections?.decay(delta);
		}
		for (let i = 0; i < (this.online ? 0 : steps); i++) {
			if (!this.stepLocal(human)) break;
		}
		if (this.replayBar) this.renderReplayBar();
		if (this.audio) {
			const running =
				!this.ended && !(this.paused && !this.online) && !(this.replay && this.replayPaused);
			if (running) this.audio.updateEngines(this.world, delta);
			else this.audio.silenceEngines();
		}

		if (this.freeCam && this.panKeys.size > 0 && !this.paused) {
			const step = (FREE_CAMERA_PAN_PER_S * delta) / 1000;
			for (const code of this.panKeys) {
				const [x, y] = PAN_KEYS[code] ?? [0, 0];
				// The keys move the view, so the map moves the other way.
				panFreeCamera(this.freeCam, -x * step, -y * step, this.worldBounds());
			}
		}

		const t1 = performance.now();
		const alpha = this.loop.alpha;
		for (const view of this.views) view.render(this.world, this.interp, alpha);
		this.hud.render(this.interp, alpha);
		if (steps > 0) this.statsHud?.update(this.world);

		if (steps > 0 && !this.attract && this.world.tick % 60 < steps) {
			document.body.dataset.simTick = String(this.world.tick);
		}
		this.frameStats.record(t1 - t0, performance.now() - t1);
	}

	/** One offline tick (local, AI or replay); false when the match or replay just ended. */
	private stepLocal(human: readonly ControlState[]): boolean {
		if (this.replay && this.world.tick >= this.replay.length) {
			this.ended = true;
			this.hooks.onReplayEnd?.();
			return false;
		}
		this.interp.capture(this.world);
		// AI decides per tick (its reaction time is measured in ticks); humans per frame.
		const controls = this.replay ? this.replayControls() : [...human];
		if (!this.replay) {
			for (const ai of this.ais) controls[ai.player] = aiControls(ai, this.world);
			this.recorded.forEach((log, p) => {
				log.push(packControls(controls[p] ?? NO_CONTROLS));
			});
		}
		const events = step(this.world, controls);
		for (const ev of events) for (const view of this.views) view.handleEvent(ev);
		this.audio?.handleEvents(events, this.world);
		return this.replay !== null || !this.checkGameOver();
	}

	/** The finished (or running) match as replay material; online: only confirmed inputs. */
	recording(): MatchRecording {
		const confirmed = this.online?.confirmedTick ?? 0;
		const inputs = this.online
			? this.online.inputLogs().map((log) => log.slice(0, confirmed))
			: this.recorded.map((log) => [...log]);
		return { ...this.setup, rules: this.world.rules, inputs };
	}

	private replayControls(): ControlState[] {
		const t = this.world.tick;
		return (this.replay?.inputs ?? []).map((log) => unpackControls(log[t] ?? 0));
	}

	private renderReplayBar(): void {
		const bar = this.replayBar;
		if (!bar || !this.replay) return;
		const at = formatClock(this.world.tick);
		const total = formatClock(this.replay.length);
		const state = this.ended ? "end" : this.replayPaused ? "paused" : `${this.replaySpeed}×`;
		const camera = this.freeCam
			? "Free camera: drag or WASD pan · wheel or +− zoom · F whole map · C follow pilots"
			: "C free camera";
		const text = `Replay · ${state} · ${at} / ${total} · Space pause · ↑↓ speed · ←→ 10 s · ${camera} · Esc menu`;
		if (bar.textContent !== text) bar.textContent = text;
		bar.dataset.tick = String(this.world.tick);
	}

	/** Replay jump: restart the scene fast-forwarded to `tick`. */
	private jumpTo(tick: number): void {
		if (!this.replay) return;
		const startTick = Math.max(0, Math.min(this.replay.length, tick));
		this.scene.restart({
			...this.startData,
			replay: {
				inputs: this.replay.inputs,
				startTick,
				...(this.freeCam ? { freeCamera: { ...this.freeCam } } : {}),
			},
		});
	}

	/** Records the result when the match is decided and ends the match after a short delay. */
	private checkGameOver(): boolean {
		if (!this.result) {
			this.result = matchResult(this.world);
			this.resultTick = this.world.tick;
			return false;
		}
		if (this.world.tick - this.resultTick < GAME_OVER_DELAY_TICKS) return false;
		// Online: only end on a result both players agree on (computed from real inputs only).
		if (this.online && this.online.confirmedTick < this.resultTick + GAME_OVER_DELAY_TICKS) {
			return false;
		}
		this.ended = true;
		this.playerInput.reset();
		if (this.attract) {
			this.scene.restart({ attract: true });
			return true;
		}
		this.hooks.onGameOver?.(this.result, this.world);
		return true;
	}

	/** Pilot names by player index ("" = the ship colour, P11.8). */
	pilotNames(): string[] {
		return [...this.names];
	}

	private pilotNamesFor(data: MatchSceneData, players: number, ai: PlayerIndex[]): string[] {
		if (this.attract) return [];
		if (this.online) return this.online.setup.names ?? [];
		if (data.names) return data.names;
		// Humans are the lowest player indices: each uses that local player's name.
		const own = loadSettings().pilotNames;
		return Array.from({ length: players }, (_, i) => (ai.includes(i) ? "" : (own[i] ?? "")));
	}

	/**
	 * Settings changed while this match runs (pause menu → Settings, P10.11): new key bindings
	 * and camera options take effect at once.
	 */
	applySettings(settings: Settings): void {
		if (this.attract) return;
		this.playerInput.destroy();
		this.playerInput = new PlayerInput(this.humanInputs, settings.bindings);
		for (const view of this.views) {
			view.cameraStyle = { lookAhead: settings.lookAhead, speedZoom: settings.speedZoom };
		}
	}

	pause(): void {
		this.setPaused(true);
	}

	setPaused(paused: boolean): void {
		if (paused === this.paused || (paused && this.ended)) return;
		this.paused = paused;
		this.playerInput.reset(); // no stuck keys on resume
		this.touch?.reset();
		this.loop.reset(); // don't fast-forward the paused time
		this.hooks.onPauseChange?.(paused);
	}

	private readonly onKeyDown = (ev: KeyboardEvent): void => {
		if (!ev.repeat && ev.code === "KeyM" && !isTypingTarget(ev)) this.audio?.toggleMute();
		if (!this.replay || this.paused || isTypingTarget(ev)) return;
		const speedIndex = REPLAY_SPEEDS.indexOf(this.replaySpeed as (typeof REPLAY_SPEEDS)[number]);
		switch (ev.code) {
			case "Space":
				if (!ev.repeat) this.replayPaused = !this.replayPaused;
				break;
			case "ArrowUp":
				this.replaySpeed = REPLAY_SPEEDS[Math.min(REPLAY_SPEEDS.length - 1, speedIndex + 1)] ?? 1;
				break;
			case "ArrowDown":
				this.replaySpeed = REPLAY_SPEEDS[Math.max(0, speedIndex - 1)] ?? 1;
				break;
			case "ArrowLeft":
				this.jumpTo(this.world.tick - REPLAY_JUMP_TICKS);
				break;
			case "ArrowRight":
				this.jumpTo(this.world.tick + REPLAY_JUMP_TICKS);
				break;
			case "KeyC":
				if (ev.repeat) return;
				this.freeCam = this.freeCam ? null : this.fitCamera();
				this.panKeys.clear();
				this.applyReplayCamera();
				break;
			default:
				if (!this.freeCam || !this.freeCameraKey(ev)) return;
		}
		ev.preventDefault();
	};

	private readonly onKeyUp = (ev: KeyboardEvent): void => {
		this.panKeys.delete(ev.code);
	};

	/** Free camera keys: WASD pan (held), +/− zoom, F the whole map. */
	private freeCameraKey(ev: KeyboardEvent): boolean {
		const cam = this.freeCam;
		if (!cam) return false;
		const { width, height } = this.scale;
		switch (ev.code) {
			case "KeyW":
			case "KeyA":
			case "KeyS":
			case "KeyD":
				this.panKeys.add(ev.code);
				return true;
			case "Equal":
			case "NumpadAdd":
				zoomFreeCamera(
					cam,
					FREE_CAMERA_ZOOM_STEP,
					{ x: 0, y: 0 },
					width,
					height,
					this.worldBounds(),
				);
				return true;
			case "Minus":
			case "NumpadSubtract":
				zoomFreeCamera(
					cam,
					1 / FREE_CAMERA_ZOOM_STEP,
					{ x: 0, y: 0 },
					width,
					height,
					this.worldBounds(),
				);
				return true;
			case "KeyF":
				Object.assign(cam, this.fitCamera());
				return true;
			default:
				return false;
		}
	}

	private worldBounds(): { width: number; height: number } {
		return this.world.size;
	}

	private fitCamera(): FreeCamera {
		return fitWorld(this.scale.width, this.scale.height, this.worldBounds());
	}

	/**
	 * Replays: the free camera gets the whole screen in the first viewport, the others hide;
	 * following the pilots restores the split screen.
	 */
	private applyReplayCamera(): void {
		const { width, height } = this.scale;
		const layouts = splitViewports(width, height, this.viewers);
		this.views.forEach((view, i) => {
			const free = this.freeCam !== null;
			view.freeCamera = i === 0 ? this.freeCam : null;
			view.camera.setVisible(!free || i === 0);
			const layout = layouts[i];
			if (free && i === 0) view.setRect({ x: 0, y: 0, width, height });
			else if (layout) view.setRect(layout);
		});
		// One full-screen view: no split divider, one minimap.
		const first = this.viewers[0];
		this.hud.setLayout(
			width,
			height,
			this.freeCam && first !== undefined
				? [{ x: 0, y: 0, width, height, player: first }]
				: layouts,
		);
		this.isolateLayers();
		document.body.dataset.replayCamera = this.freeCam ? "free" : "follow";
	}

	/** Free camera: drag to pan, the wheel or a two-finger pinch to zoom around the pointer. */
	private attachFreeCameraPointer(): void {
		this.input.addPointer(1);
		const anchor = (x: number, y: number) => ({
			x: x - this.scale.width / 2,
			y: y - this.scale.height / 2,
		});
		this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
			const cam = this.freeCam;
			if (!cam || this.paused) return;
			const a = this.input.pointer1;
			const b = this.input.pointer2;
			if (a.isDown && b.isDown) {
				const dist = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
				if (this.pinch && dist > 0) {
					const mid = anchor((a.x + b.x) / 2, (a.y + b.y) / 2);
					const { width, height } = this.scale;
					zoomFreeCamera(cam, dist / this.pinch, mid, width, height, this.worldBounds());
				}
				this.pinch = dist;
				return;
			}
			this.pinch = null;
			if (!pointer.isDown) return;
			panFreeCamera(
				cam,
				pointer.x - pointer.prevPosition.x,
				pointer.y - pointer.prevPosition.y,
				this.worldBounds(),
			);
		});
		this.input.on(Phaser.Input.Events.POINTER_UP, () => {
			this.pinch = null;
		});
		this.input.on(
			Phaser.Input.Events.POINTER_WHEEL,
			(pointer: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
				const cam = this.freeCam;
				if (!cam || this.paused) return;
				const { width, height } = this.scale;
				const factor = Math.exp(-dy * FREE_CAMERA_WHEEL);
				zoomFreeCamera(
					cam,
					factor,
					anchor(pointer.x, pointer.y),
					width,
					height,
					this.worldBounds(),
				);
			},
		);
	}

	/** Each camera renders only its own layer. */
	private isolateLayers(): void {
		const layers = [...this.views.map((v) => v.layer), this.hud.layer];
		const cameras = [...this.views.map((v) => v.camera), this.hud.camera];
		cameras.forEach((camera, i) => {
			camera.ignore(layers.filter((_, j) => j !== i));
		});
	}

	private onResize(size: Phaser.Structs.Size): void {
		const layouts = splitViewports(size.width, size.height, this.viewers);
		layouts.forEach((layout, i) => {
			this.views[i]?.setRect(layout);
		});
		this.hud.setLayout(size.width, size.height, layouts);
		this.isolateLayers();
		if (this.replay) this.applyReplayCamera();
	}
}

/** Ticks as m:ss. */
function formatClock(ticks: number): string {
	const seconds = Math.floor((ticks * TICK_MS) / 1000);
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
